/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { AuthorableComponent } from '@core/aem/models/authorable-component.model';

import { CtaLink } from './models/cta-link.interface';
import { Router } from '@angular/router';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { APP_PERMISSION } from '@support/shared/constants/device.constants';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';

@Component({
  selector: 'tmo-cta-link',
  templateUrl: './cta-link.component.html',
})

/**
 * A T-mobile custom design legalText component.
 * A tmo-legal-text component can be used overall the application to display the legal disclaimer
 * which is fetched from AEM
 */
export class CtaLinkComponent extends AuthorableComponent<CtaLink> implements OnInit, OnDestroy {
  @Input() public text: String;
  @Input() public link: String;
  @Input() public target: String;
  @Input() public anchorId: String;
  @Input() public css: string;
  @Input() public impression: boolean;

  /**
   * Determines wheter we should use a routerLink for internal link
   * or href for external link
   */
  public internalLink: boolean;
  /**
   * flag to store permission
   */
  public isPermissionAvailable: boolean;
  /**
   * flag to store permission
   */
  public isDevicePermissionAvailable: boolean;
  /**
   * Whenever this component needs some value to be instantiated on load then
   * values of its properties are initialized in the constructor
   * @returns void
   */
  constructor(
    private featureToggleUtilService: FeatureToggleUtilService,
    public router: Router
  ) {
    super();
    this.isPermissionAvailable = true;
    this.isDevicePermissionAvailable = true;
  }

  /**
   * Called whenever this component is invoked from parent component
   *
   * @memberof CtaLinkComponent
   */
  public ngOnInit(): void {}

  /**
   * Sets data for text, link and css fields
   * @param data: CtaLink
   * @returns void
   */
  public setData(data: CtaLink): void {
    if (this.validate(data)) {
      this.text = data.text;
      this.link = data.link;
      this.css = data.css || '';

      // if the link starts with / we can assume its an internal link.
      // Because, external links start with https://.
      // and all internal relative links start with /
      if (this.link.startsWith('/')) {
        this.internalLink = true;
      } else {
        this.internalLink = false;
      }

      if (data.text === APP_PERMISSION.SHOP_NOW || data.text === APP_PERMISSION.BROWSE_PHONES) {
        this.isPermissionAvailable = this.featureToggleUtilService.getFraudLockPermission(
          SHARED_CONSTANTS.GLOBAL,
          PERMISSION_CONSTANTS.ORDER_DEVICE
        );
        this.isDevicePermissionAvailable = this.featureToggleUtilService.getFraudLockPermission(
          SHARED_CONSTANTS.GLOBAL,
          PERMISSION_CONSTANTS.PRE_ORDER_DEVICE
        );
      }
    }
    // assign optionals
    this.target = data.target;
    this.anchorId = data.anchorId;
    this.impression = data.impression;
  }

  /**
   * Check if the data is valid
   * should throw an error if the input doesn't match designated fields
   *
   * @param {CtaLink} data
   * @returns {boolean}
   * @memberof CtaLinkComponent
   */
  public validate(data: CtaLink): boolean {
    if (!data.text) {
      throw new Error(`Invalid options for text ${data}: Required fields [text]`);
    }
    return true;
  }

  /**
   * Destroys subscriptions
   *
   * @memberof CtaLinkComponent
   */
  public ngOnDestroy(): void {}
}
